import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Layout from "../../components/Layout";
import Loader from "../../components/Loader";
import TodoCalendar from "./TodoCalendar";
import {
    IconCalendar,
    IconChat,
    IconChevron,
    IconEye,
    IconEyeOff,
    IconLeave,
    IconList,
    IconPin,
    IconPlus,
    IconRefresh,
    IconTrash,
    IconUsers,
} from "./TodoIcons";
import TodoTaskPanel from "./TodoTaskPanel";
import { TodoChatPickerModal, TodoListModal } from "./TodoModals";
import useTodoData, { pickQuick } from "./useTodoData";
import {
    addAttachment,
    addComment,
    createTask,
    deleteAttachment,
    deleteComment,
    deleteList,
    deleteTask,
    fetchCalendar,
    leaveList,
    retryTranscript,
    setListState,
    updateTask,
} from "./todoApi";
import {
    activeCount,
    buildMonthGrid,
    dueText,
    errorText,
    firstOfMonth,
    fromInputValue,
    getId,
    isListHidden,
    isListPinned,
    isOlderVersion,
    isOverdue,
    nextStatus,
    repeatLabel,
    splitTasks,
    statusLabel,
    userName,
} from "./todoUtils";

/*
 * Раздел «Задачи» в админке — тот же, что в приложении: списки с участниками,
 * задачи со сроком, напоминанием и повтором, подзадачи, файлы, голосовые с
 * расшифровкой, комментарии, календарь, отправка в чат.
 *
 * Слева — списки, посередине — задачи списка или календарь, справа — открытая задача.
 * Календарей два: общий (все списки) и свой у каждого списка — только его задачи.
 */

/** После своих правок и событий сервера календарь перечитывается с такой паузой. */
const CALENDAR_RELOAD_MS = 500;

/** Строка задачи в списке. */
function TaskRow({ task, subtasks = [], meId, selected, onOpen, onStatus }) {
    const overdue = isOverdue(task);
    const done = subtasks.filter((item) => item.status === "done").length;

    const meta = [
        task.assignee ? userName(task.assignee, meId) : "",
        task.dueAt ? dueText(task.dueAt) : "",
        task.repeat?.every && task.repeat.every !== "none" ? repeatLabel(task.repeat.every).toLowerCase() : "",
        subtasks.length ? `подзадачи ${done}/${subtasks.length}` : "",
        task.attachments?.length ? `файлы: ${task.attachments.length}` : "",
        task.comments?.length ? `комментарии: ${task.comments.length}` : "",
    ].filter(Boolean);

    return (
        <div className={["todoRow", `is-${task.status}`, selected ? "is-selected" : ""].filter(Boolean).join(" ")}>
            <button
                type="button"
                className={`todoRow__status is-${task.status}`}
                title={`${statusLabel(task.status)} → ${statusLabel(nextStatus(task.status))}`}
                onClick={() => onStatus(task, nextStatus(task.status))}
                aria-label="Сменить статус"
            />
            <button type="button" className="todoRow__main" onClick={() => onOpen(task)}>
                <span className="todoRow__title">
                    {task.pinned && task.status !== "done" ? <i className="todoRow__pin" title="Закреплена">📌</i> : null}
                    {task.priority === "high" ? <b className="todoRow__high" title="Важная">!</b> : null}
                    {task.title}
                </span>
                {meta.length ? (
                    <span className={`todoRow__meta ${overdue ? "is-overdue" : ""}`}>
                        {overdue ? "просрочено · " : ""}{meta.join(" · ")}
                    </span>
                ) : null}
            </button>
        </div>
    );
}

export default function TodoPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const meId = useSelector((state) => String(state.auth?.user?._id || state.auth?.user?.id || ""));
    const chats = useSelector((state) => state.chat?.chats);

    const {
        lists, listsLoaded, listsError, tasksByList, tasksLoading, tasksError, revision,
        loadTasks, reloadAll, applyList, removeList, applyTask, removeTasks,
        overlayQuick, startQuick, finishQuick,
    } = useTodoData(meId);

    /** «lists» — списки, «calendar» — общий календарь по всем спискам. */
    const [view, setView] = useState(searchParams.get("view") === "calendar" ? "calendar" : "lists");
    /** Что показано у открытого списка: его задачи или его календарь. */
    const [listView, setListView] = useState(searchParams.get("view") === "list-calendar" ? "calendar" : "tasks");
    const [selectedListId, setSelectedListId] = useState(searchParams.get("list") || "");
    /** Открытая задача: { listId, taskId }. */
    const [openTask, setOpenTask] = useState(() => {
        const taskId = searchParams.get("task") || "";
        const listId = searchParams.get("list") || "";
        return taskId && listId ? { listId, taskId } : null;
    });
    const [showHidden, setShowHidden] = useState(false);
    const [showDone, setShowDone] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newDue, setNewDue] = useState("");
    const [adding, setAdding] = useState(false);
    const [pageError, setPageError] = useState("");
    const [notice, setNotice] = useState("");
    /** Окно списка: null — закрыто, { list } — правка, { list: null } — новый. */
    const [listModal, setListModal] = useState(null);
    const [shareTarget, setShareTarget] = useState(null);

    const [month, setMonth] = useState(() => firstOfMonth(new Date()));
    const [calendar, setCalendar] = useState(null);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState("");
    const [calendarNonce, setCalendarNonce] = useState(0);

    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = window.setTimeout(() => setNotice(""), 2600);
        return () => window.clearTimeout(timer);
    }, [notice]);

    // ---------- списки ----------

    const visibleLists = useMemo(() => lists.filter((item) => !isListHidden(item, meId)), [lists, meId]);
    const hiddenLists = useMemo(() => lists.filter((item) => isListHidden(item, meId)), [lists, meId]);

    // Первым открывается личный список; если открытый список пропал — тоже он.
    useEffect(() => {
        if (!listsLoaded || !lists.length) return;
        if (selectedListId && lists.some((item) => item._id === selectedListId)) return;
        setSelectedListId((visibleLists[0] || lists[0])._id);
    }, [lists, listsLoaded, selectedListId, visibleLists]);

    useEffect(() => {
        if (selectedListId) loadTasks(selectedListId);
    }, [loadTasks, selectedListId]);

    // Задачу могли открыть из календаря или по ссылке — её список нужен целиком (подзадачи).
    useEffect(() => {
        if (openTask?.listId) loadTasks(openTask.listId);
    }, [loadTasks, openTask]);

    // Адрес страницы помнит, что открыто: ссылку можно переслать или обновить страницу.
    useEffect(() => {
        const next = {};
        if (view === "calendar") next.view = "calendar";
        else if (listView === "calendar") next.view = "list-calendar";
        if (selectedListId) next.list = openTask?.listId || selectedListId;
        if (openTask?.taskId) next.task = openTask.taskId;

        const same = ["view", "list", "task"].every((key) => (searchParams.get(key) || "") === (next[key] || ""));
        if (!same) setSearchParams(next, { replace: true });
    }, [listView, openTask, searchParams, selectedListId, setSearchParams, view]);

    const selectedList = lists.find((item) => item._id === selectedListId) || null;
    const listTasks = selectedListId ? tasksByList[selectedListId] : null;
    const split = useMemo(() => splitTasks(listTasks || []), [listTasks]);
    const activeTasks = split.top.filter((item) => item.status !== "done");
    const doneTasks = split.top.filter((item) => item.status === "done");

    /** Сколько невыполненных: по загруженным задачам точнее, чем по счётчику списка. */
    const countOf = useCallback((list) => {
        const loaded = tasksByList[list._id];
        if (!loaded) return activeCount(list);
        return loaded.filter((item) => !item.parentId && item.status !== "done").length;
    }, [tasksByList]);

    // ---------- открытая задача ----------

    const openTaskList = openTask ? lists.find((item) => item._id === openTask.listId) || null : null;
    const openTaskItems = openTask ? tasksByList[openTask.listId] : null;
    const openTaskData = openTaskItems ? openTaskItems.find((item) => item._id === openTask.taskId) || null : null;
    const openSubtasks = useMemo(() => {
        if (!openTask || !openTaskItems) return [];
        return openTaskItems
            .filter((item) => item.parentId === openTask.taskId)
            .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")));
    }, [openTask, openTaskItems]);

    // Задачу удалили (здесь или с телефона) — панель закрываем.
    useEffect(() => {
        if (!openTask || !openTaskItems || tasksLoading[openTask.listId]) return;
        if (!openTaskItems.some((item) => item._id === openTask.taskId)) setOpenTask(null);
    }, [openTask, openTaskItems, tasksLoading]);

    const touchCalendar = useCallback(() => setCalendarNonce((value) => value + 1), []);

    /** Что сейчас на экране — чтобы действия не пересоздавались при каждой правке. */
    const shownRef = useRef({ tasksByList, calendar });
    shownRef.current = { tasksByList, calendar };

    /** Действия над задачами: запрос на сервер и сразу — результат на экран. */
    const actions = useMemo(() => {
        const findShown = (taskId) => {
            const shown = shownRef.current;
            const lists = Object.values(shown.tasksByList);

            for (let index = 0; index < lists.length; index += 1) {
                const found = lists[index].find((item) => item._id === taskId);
                if (found) return found;
            }
            return (shown.calendar?.tasks || []).find((item) => item._id === taskId) || null;
        };

        const patchCalendar = (taskId, values) => setCalendar((current) => (current
            ? { ...current, tasks: current.tasks.map((item) => (item._id === taskId ? { ...item, ...values } : item)) }
            : current));

        const apply = (task) => {
            const shown = applyTask(task) || task;
            // В календаре правка видна сразу, а следом он перечитывается целиком.
            setCalendar((current) => (current && shown?._id
                ? {
                    ...current,
                    tasks: current.tasks.map(
                        (item) => (item._id === shown._id && !isOlderVersion(shown, item) ? shown : item)
                    ),
                }
                : current));
            touchCalendar();
            return task;
        };

        return {
            createTask: async (listId, draft) => apply(await createTask(listId, draft)),
            updateTask: async (taskId, patch) => {
                // Статус, «важная» и «закрепить» переключаются на экране сразу:
                // ждать ответа сервера незачем. Не сохранилось — вернём как было.
                const quick = pickQuick(patch);
                const shown = quick ? findShown(taskId) : null;

                if (shown) {
                    startQuick(shown, quick);
                    patchCalendar(taskId, quick);
                }

                try {
                    const saved = await updateTask(taskId, patch);
                    if (shown) finishQuick(taskId);
                    return apply(saved);
                } catch (error) {
                    const back = shown ? finishQuick(taskId, true) : null;
                    if (back) patchCalendar(taskId, back);
                    throw error;
                }
            },
            deleteTask: async (task) => {
                const ids = await deleteTask(task._id);
                removeTasks(task.listId, ids);
                touchCalendar();
                return true;
            },
            addComment: async (taskId, text, attachmentId) => apply(await addComment(taskId, text, attachmentId)),
            deleteComment: async (taskId, commentId) => apply(await deleteComment(taskId, commentId)),
            addAttachment: async (taskId, file, onProgress) => apply(await addAttachment(taskId, file, onProgress)),
            deleteAttachment: async (taskId, attachmentId) => apply(await deleteAttachment(taskId, attachmentId)),
            retryTranscript: async (taskId, attachmentId) => apply(await retryTranscript(taskId, attachmentId)),
        };
    }, [applyTask, finishQuick, removeTasks, startQuick, touchCalendar]);

    const changeStatus = useCallback(async (task, status) => {
        setPageError("");
        try {
            await actions.updateTask(task._id, { status });
        } catch (error) {
            setPageError(errorText(error, "Не удалось сменить статус"));
        }
    }, [actions]);

    const addTask = async () => {
        const clean = newTitle.trim();
        if (!clean || !selectedListId || adding) return;

        setAdding(true);
        setPageError("");

        try {
            const dueAt = fromInputValue(newDue);
            await actions.createTask(selectedListId, { title: clean, ...(dueAt ? { dueAt } : {}) });
            setNewTitle("");
            setNewDue("");
        } catch (error) {
            setPageError(errorText(error, "Не удалось добавить задачу"));
        } finally {
            if (aliveRef.current) setAdding(false);
        }
    };

    // ---------- действия со списком ----------

    const runList = async (job, fallback) => {
        setPageError("");
        try {
            await job();
        } catch (error) {
            setPageError(errorText(error, fallback));
        }
    };

    const togglePin = (list) => runList(
        async () => applyList(await setListState(list._id, { pinned: !isListPinned(list, meId) })),
        "Не удалось закрепить список"
    );

    const toggleHidden = (list) => runList(
        async () => applyList(await setListState(list._id, { hidden: !isListHidden(list, meId) })),
        "Не удалось скрыть список"
    );

    const removeOrLeave = (list) => {
        const own = String(list.ownerId) === meId;
        const question = own
            ? `Удалить список «${list.title}» вместе со всеми задачами? Его потеряют все участники.`
            : `Выйти из списка «${list.title}»? Его задачи вы больше не увидите.`;
        if (!window.confirm(question)) return;

        runList(async () => {
            if (own) await deleteList(list._id);
            else await leaveList(list._id);
            removeList(list._id);
            touchCalendar();
        }, own ? "Не удалось удалить список" : "Не удалось выйти из списка");
    };

    // ---------- календарь ----------

    /** Чей календарь открыт: null — никакой, "" — общий, id — календарь списка. */
    const calendarScope = view === "calendar"
        ? ""
        : (listView === "calendar" && selectedListId ? selectedListId : null);

    // Открыли другой календарь — задачи прежнего на экране не оставляем.
    useEffect(() => {
        setCalendar(null);
        setCalendarError("");
    }, [calendarScope]);

    useEffect(() => {
        if (calendarScope === null || !meId) return undefined;
        let cancelled = false;

        // Сетка показывает и края соседних месяцев — берём задачи за всю сетку.
        const grid = buildMonthGrid(month);
        const from = grid[0].date;
        const to = new Date(grid[grid.length - 1].date.getTime() + 24 * 60 * 60 * 1000);

        const timer = window.setTimeout(() => {
            setCalendarLoading(true);
            fetchCalendar(from, to, calendarScope)
                .then((result) => {
                    if (cancelled) return;
                    // Что нажато, но ещё не подтверждено сервером, остаётся на экране.
                    setCalendar({ ...result, tasks: result.tasks.map(overlayQuick) });
                    setCalendarError("");
                })
                .catch((error) => {
                    if (!cancelled) setCalendarError(errorText(error, "Не удалось загрузить календарь"));
                })
                .finally(() => {
                    if (!cancelled) setCalendarLoading(false);
                });
        }, calendar ? CALENDAR_RELOAD_MS : 0);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // calendar в зависимостях не нужен: он только выбирает паузу перед запросом.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarNonce, calendarScope, meId, month, overlayQuick, revision]);

    const openFromCalendar = (task) => setOpenTask({ listId: task.listId, taskId: task._id });

    // ---------- разметка ----------

    const renderListRow = (list) => {
        const pinned = isListPinned(list, meId);
        const hidden = isListHidden(list, meId);
        const count = countOf(list);
        const people = Array.isArray(list.members) ? list.members.length : 0;

        return (
            <button
                key={list._id}
                type="button"
                className={[
                    "todoList",
                    view === "lists" && list._id === selectedListId ? "is-selected" : "",
                    hidden ? "is-hidden" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => {
                    setSelectedListId(list._id);
                    setView("lists");
                }}
            >
                <span className={`todoList__badge ${list.isPersonal ? "is-personal" : ""}`} aria-hidden="true">
                    {String(list.title || "С").trim().charAt(0).toLocaleUpperCase("ru-RU")}
                </span>
                <span className="todoList__main">
                    <span className="todoList__title">
                        {list.title}
                        {pinned ? <i title="Закреплён"><IconPin filled size={11} /></i> : null}
                    </span>
                    <span className="todoList__meta">
                        {list.isPersonal ? "личный" : `участников: ${people}`}
                    </span>
                </span>
                {count > 0 ? <span className="todoList__count" title="Невыполненных задач">{count}</span> : null}
            </button>
        );
    };

    /** Шапка открытого списка: название, участники, «Задачи / Календарь» и действия со списком. */
    const renderListHead = (list) => {
        const pinned = isListPinned(list, meId);
        const hidden = isListHidden(list, meId);
        const own = String(list.ownerId) === meId;
        const members = (list.members || []).map((user) => userName(user, meId)).join(", ");

        return (
            <div className="todoPage__listHead">
                <div className="todoPage__listTitle">
                    <h2>{list.title}</h2>
                    <p>
                        {list.isPersonal
                            ? "Личный список — его видите только вы."
                            : `Участники: ${members || "только вы"}`}
                    </p>
                </div>

                <div className="todoPage__listTools">
                    <div className="todoSeg" role="tablist" aria-label="Что показать">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={listView === "tasks"}
                            className={listView === "tasks" ? "is-active" : ""}
                            onClick={() => setListView("tasks")}
                        >
                            <IconList size={16} /> Задачи
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={listView === "calendar"}
                            className={listView === "calendar" ? "is-active" : ""}
                            onClick={() => setListView("calendar")}
                            title="Календарь этого списка: только его задачи, по дням и времени"
                        >
                            <IconCalendar size={16} /> Календарь
                        </button>
                    </div>

                    <span className="todoPage__listActions">
                        <button
                            type="button"
                            className={pinned ? "is-on" : ""}
                            onClick={() => togglePin(list)}
                            title={pinned ? "Открепить" : "Закрепить наверху"}
                        >
                            <IconPin filled={pinned} />
                        </button>
                        {!list.isPersonal ? (
                            <button type="button" onClick={() => toggleHidden(list)} title={hidden ? "Вернуть в списки" : "Скрыть у себя"}>
                                {hidden ? <IconEye /> : <IconEyeOff />}
                            </button>
                        ) : null}
                        {own ? (
                            <button type="button" onClick={() => setListModal({ list })} title="Название и участники">
                                <IconUsers />
                            </button>
                        ) : null}
                        {!list.isPersonal ? (
                            <button
                                type="button"
                                className="is-danger"
                                onClick={() => removeOrLeave(list)}
                                title={own ? "Удалить список" : "Выйти из списка"}
                            >
                                {own ? <IconTrash /> : <IconLeave />}
                            </button>
                        ) : null}
                    </span>
                </div>
            </div>
        );
    };

    /** Календарь: общий (list = null) или одного списка. */
    const renderCalendar = (list) => (
        <TodoCalendar
            month={month}
            onMonthChange={setMonth}
            calendar={calendar}
            loading={calendarLoading}
            error={calendarError}
            lists={lists}
            scopeList={list}
            meId={meId}
            defaultListId={selectedListId}
            selectedTaskId={openTask?.taskId || ""}
            onOpenTask={openFromCalendar}
            onStatus={changeStatus}
            onAddTask={async (listId, draft) => {
                try {
                    await actions.createTask(listId, draft);
                } catch (error) {
                    throw new Error(errorText(error, "Не удалось добавить задачу"));
                }
            }}
        />
    );

    const renderTasks = () => {
        if (!selectedList) {
            return <div className="todoPage__placeholder">{listsLoaded ? "Выберите список слева." : <Loader />}</div>;
        }

        const loading = !!tasksLoading[selectedListId] && !listTasks;
        const loadError = tasksError[selectedListId];

        if (listView === "calendar") {
            return (
                <>
                    {renderListHead(selectedList)}
                    {renderCalendar(selectedList)}
                </>
            );
        }

        return (
            <>
                {renderListHead(selectedList)}

                <form
                    className="todoPage__add"
                    onSubmit={(event) => {
                        event.preventDefault();
                        addTask();
                    }}
                >
                    <input
                        type="text"
                        value={newTitle}
                        maxLength={300}
                        placeholder="Новая задача — напишите и нажмите Enter"
                        onChange={(event) => setNewTitle(event.target.value)}
                    />
                    <input
                        type="datetime-local"
                        value={newDue}
                        onChange={(event) => setNewDue(event.target.value)}
                        title="Срок (необязательно)"
                        aria-label="Срок"
                    />
                    <button type="submit" disabled={!newTitle.trim() || adding}>
                        <IconPlus size={16} /> {adding ? "Добавляем…" : "Добавить"}
                    </button>
                </form>

                {loadError ? <div className="todoPage__error" role="alert">{loadError}</div> : null}
                {loading ? <div className="todoPage__placeholder"><Loader /></div> : null}

                {!loading && listTasks && !split.top.length ? (
                    <div className="todoPage__placeholder">В этом списке пока нет задач.</div>
                ) : null}

                <div className="todoPage__rows">
                    {activeTasks.map((task) => (
                        <TaskRow
                            key={task._id}
                            task={task}
                            subtasks={split.children[task._id] || []}
                            meId={meId}
                            selected={openTask?.taskId === task._id}
                            onOpen={(item) => setOpenTask({ listId: item.listId, taskId: item._id })}
                            onStatus={changeStatus}
                        />
                    ))}
                </div>

                {doneTasks.length ? (
                    <>
                        <button type="button" className="todoPage__doneToggle" onClick={() => setShowDone((value) => !value)}>
                            <IconChevron open={showDone} size={14} /> Выполнено · {doneTasks.length}
                        </button>
                        {showDone ? (
                            <div className="todoPage__rows">
                                {doneTasks.map((task) => (
                                    <TaskRow
                                        key={task._id}
                                        task={task}
                                        subtasks={split.children[task._id] || []}
                                        meId={meId}
                                        selected={openTask?.taskId === task._id}
                                        onOpen={(item) => setOpenTask({ listId: item.listId, taskId: item._id })}
                                        onStatus={changeStatus}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </>
        );
    };

    return (
        <Layout sidebar>
            <div className="todoPage">
                <header className="todoPage__header">
                    <h1>Задачи</h1>

                    <div className="todoPage__views todoSeg" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === "lists"}
                            className={view === "lists" ? "is-active" : ""}
                            onClick={() => setView("lists")}
                        >
                            <IconList size={16} /> Списки
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === "calendar"}
                            className={view === "calendar" ? "is-active" : ""}
                            onClick={() => setView("calendar")}
                            title="Задачи со сроком из всех списков"
                        >
                            <IconCalendar size={16} /> Общий календарь
                        </button>
                    </div>

                    <div className="todoPage__headerActions">
                        <Link to="/chat" title="Перейти в чаты"><IconChat size={16} /> Чаты</Link>
                        <button type="button" onClick={reloadAll} title="Перечитать задачи с сервера" aria-label="Обновить">
                            <IconRefresh size={16} />
                        </button>
                    </div>
                </header>

                {listsError ? <div className="todoPage__error" role="alert">{listsError}</div> : null}
                {pageError ? <div className="todoPage__error" role="alert">{pageError}</div> : null}
                {notice ? <div className="todoPage__notice" role="status">{notice}</div> : null}

                <div className={`todoPage__body ${openTask ? "has-panel" : ""}`}>
                    <nav className="todoPage__lists" aria-label="Списки задач">
                        <div className="todoPage__listsHead">
                            <span>Списки</span>
                            <button
                                type="button"
                                className="todoPage__newList"
                                onClick={() => setListModal({ list: null })}
                                title="Новый список"
                            >
                                <IconPlus size={15} /> Новый
                            </button>
                        </div>

                        {!listsLoaded && !listsError ? <Loader /> : null}

                        {visibleLists.map(renderListRow)}

                        {hiddenLists.length ? (
                            <>
                                <button type="button" className="todoPage__hiddenToggle" onClick={() => setShowHidden((value) => !value)}>
                                    <IconChevron open={showHidden} size={14} /> Скрытые · {hiddenLists.length}
                                </button>
                                {showHidden ? hiddenLists.map(renderListRow) : null}
                            </>
                        ) : null}
                    </nav>

                    <main className="todoPage__main">
                        {view === "calendar" ? renderCalendar(null) : renderTasks()}
                    </main>

                    {openTask ? (
                        openTaskData ? (
                            <TodoTaskPanel
                                task={openTaskData}
                                subtasks={openSubtasks}
                                list={openTaskList}
                                meId={meId}
                                actions={actions}
                                onClose={() => setOpenTask(null)}
                                onShare={(task) => setShareTarget(task)}
                            />
                        ) : (
                            <aside className="todoPanel">
                                <div className="todoPanel__head">
                                    <span className="todoPanel__list">Задача</span>
                                    <span className="todoPanel__headActions">
                                        <button type="button" onClick={() => setOpenTask(null)} aria-label="Закрыть задачу">✕</button>
                                    </span>
                                </div>
                                <div className="todoPage__placeholder">
                                    {tasksError[openTask.listId] || <Loader />}
                                </div>
                            </aside>
                        )
                    ) : null}
                </div>
            </div>

            {listModal ? (
                <TodoListModal
                    list={listModal.list}
                    chats={chats}
                    meId={meId}
                    onClose={() => setListModal(null)}
                    onSaved={(saved) => {
                        applyList(saved);
                        setListModal(null);
                        if (!listModal.list && saved?._id) {
                            setSelectedListId(saved._id);
                            setView("lists");
                        }
                    }}
                />
            ) : null}

            {shareTarget ? (
                <TodoChatPickerModal
                    task={shareTarget}
                    subtasks={openSubtasks}
                    chats={chats}
                    meId={meId}
                    onClose={() => setShareTarget(null)}
                    onSent={({ mode, task }) => {
                        if (task) applyTask(task);
                        setShareTarget(null);
                        setNotice(mode === "card" ? "Задача отправлена в чат карточкой" : "Задача отправлена в чат текстом");
                    }}
                />
            ) : null}
        </Layout>
    );
}
